import React, { useCallback, useEffect, useRef, useState } from "react";

import xmarkIcon from "../assets/xmark.svg";
import sparksIcon from "../assets/sparks.svg";
import settingsIcon from "../assets/settings.svg";
import attachmentIcon from "../assets/attachment.svg";
import { checkServerConfig } from "../ai-integration/config";
import { runGatewayAssistantTurn } from "../ai-integration/turn";
import GatewayAIModelSettings from "./GatewayAIModelSettings";
import GatewayAIConfirmCard from "./GatewayAIConfirmCard";
import SimpleMarkdown from "./SimpleMarkdown";
import { gatewayAIEmptyCopy, gatewayAIPromptOptions } from "./gatewayAIPrompts";
import { consume as consumePending, clearForPopover } from "../ai-integration/pendingActionRegistry";
import { dispatch as dispatchMutation } from "../ai-integration/mutationDispatcher";
import {
  confirmationFromPending,
  createAssistantRecorder,
} from "../ai-integration/assistantHistory";
import {
  createAgentConversationStore,
  readActiveConversationId,
} from "../ai-integration/agentConversation";
import {
  appendAgentMessage,
  createAgentConversation,
  getAgentConversation,
  getAiPreference,
  listAgentConversations,
  listAiProviders,
  saveAssistantConversation,
} from "../api";
import "../styles/GatewayAIPopover.css";

const TOOL_VERB = {
  list_datasources: "Listing datasources",
  describe_datasource: "Inspecting datasource",
  ping_datasource: "Pinging datasource",
  list_db_schemas: "Listing schemas",
  list_db_tables: "Listing tables",
  list_db_columns: "Listing columns",
  list_api_profiles: "Listing API profiles",
  list_profile_tags: "Listing profile tags",
  list_credentials: "Listing credentials",
  test_credential: "Testing credential",
  list_oauth_providers: "Listing OAuth providers",
  list_oauth_apps: "Listing OAuth apps",
  get_oauth_app: "Inspecting OAuth app",
  list_mqtt_brokers: "Listing MQTT brokers",
  get_mqtt_broker_state: "Checking broker state",
  list_mqtt_subscriptions: "Listing subscriptions",
  list_collectors: "Listing custom connectors",
  get_collector_script: "Reading the collector script",
  set_collector_script: "Writing the script into the editor",
  list_users: "Listing users",
  list_groups: "Listing groups",
  get_group: "Inspecting group",
  list_projects: "Listing projects",
  get_workspace_settings: "Reading workspace settings",
  list_ai_providers: "Listing AI providers",
  get_ai_preference: "Reading AI preference",
  list_known_models: "Looking up canonical model names",
  propose_ai_provider: "Opening AI provider form",
  get_supported_connector_types: "Looking up connector types",
  navigate_to: "Navigating",
  highlight_element: "Highlighting",
  propose_datasource: "Opening connection form",
  propose_api_profile: "Opening API profile form",
  propose_credential: "Opening credential form",
  propose_oauth_app: "Opening OAuth app form",
  propose_mqtt_broker: "Opening MQTT broker form",
  propose_user: "Opening user form",
  propose_group: "Opening group form",
  propose_project: "Opening project form",
  list_form_fields: "Inspecting form fields",
  list_select_options: "Reading dropdown options",
  set_fields: "Filling fields",
  set_field: "Filling field",
  click_element: "Clicking",
  request_confirmation: "Asking for confirmation",
};

function describeToolEvent(name, phase) {
  if (!name || name === "final_answer") return null;
  const verb = TOOL_VERB[name];
  if (!verb) return phase === "result" ? `${name} ✓` : `Running ${name}…`;
  return phase === "result" ? `${verb} ✓` : `${verb}…`;
}

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

const DRAWER_WIDTH_STORAGE_KEY = "gw-ai-drawer-width";
const DRAWER_MIN_WIDTH = 360;
const DRAWER_MAX_WIDTH = 900;
const DRAWER_DEFAULT_WIDTH = 440;

function loadInitialDrawerWidth() {
  try {
    const raw = window.localStorage.getItem(DRAWER_WIDTH_STORAGE_KEY);
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n) && n >= DRAWER_MIN_WIDTH && n <= DRAWER_MAX_WIDTH) {
      return n;
    }
  } catch {}
  return DRAWER_DEFAULT_WIDTH;
}

export default function GatewayAIPopover({ open, onClose, ctx, initialPrompt, onConsumedInitialPrompt }) {
  const emptyCopy = gatewayAIEmptyCopy(ctx?.activeSection);
  const suggestedPrompts = gatewayAIPromptOptions(ctx?.activeSection);
  const [prompt, setPrompt] = useState("");
  // Consume an inbound prompt (e.g. from the Journal's Ask AI button)
  // on each open — set the textarea, scroll into view, and clear the
  // upstream state so re-opens don't replay it.
  useEffect(() => {
    if (!open || !initialPrompt) return;
    setPrompt(initialPrompt);
    onConsumedInitialPrompt?.();
  }, [open, initialPrompt, onConsumedInitialPrompt]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [statusMessage, setStatusMessage] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(loadInitialDrawerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const widthRef = useRef(drawerWidth);
  const abortRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const settingsAnchorRef = useRef(null);

  // Sync the CSS variable on mount + whenever React width state changes (not
  // during drag — drag writes the variable directly for zero-lag feedback).
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--gw-ai-drawer-width",
      `${drawerWidth}px`
    );
    try {
      window.localStorage.setItem(DRAWER_WIDTH_STORAGE_KEY, String(drawerWidth));
    } catch {}
  }, [drawerWidth]);

  const onResizerPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.target.setPointerCapture(e.pointerId);
    setIsResizing(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.documentElement.classList.add("gwAIResizing");
  }, []);

  const onResizerPointerMove = useCallback((e) => {
    if (!e.target.hasPointerCapture(e.pointerId)) return;
    e.preventDefault();
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth >= DRAWER_MIN_WIDTH && newWidth <= DRAWER_MAX_WIDTH) {
      document.documentElement.style.setProperty(
        "--gw-ai-drawer-width",
        `${newWidth}px`
      );
      widthRef.current = newWidth;
    }
  }, []);

  const onResizerPointerUp = useCallback((e) => {
    if (e.target.hasPointerCapture(e.pointerId)) {
      e.target.releasePointerCapture(e.pointerId);
    }
    setDrawerWidth(widthRef.current);
    setIsResizing(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.documentElement.classList.remove("gwAIResizing");
  }, []);

  // Stable id for this popover instance — threaded into ctx so the confirm
  // tool can scope its tokens to this instance and only this instance can
  // approve them. Cleared from the registry on unmount.
  const popoverIdRef = useRef(null);
  if (popoverIdRef.current === null) {
    popoverIdRef.current = `gw_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
  }
  useEffect(() => {
    const id = popoverIdRef.current;
    return () => {
      try {
        clearForPopover(id);
      } catch {}
    };
  }, []);

  // Conversation log. Every turn is persisted to
  // /api/ai/assistant/conversations so an operator can go back and see what
  // the assistant did — including the tool calls and mutation verdicts that
  // never reach `conversation`. The recorder swallows its own failures: the
  // chat must never stall or error because the log is unreachable.
  const recorderRef = useRef(null);
  if (recorderRef.current === null) {
    recorderRef.current = createAssistantRecorder({
      save: saveAssistantConversation,
      userName: ctx?.user?.username || ctx?.user?.email || "",
    });
  }
  useEffect(() => {
    const recorder = recorderRef.current;
    return () => recorder.dispose();
  }, []);

  // Session continuity. The recorder above writes the investigation log; this
  // store writes the resumable thread, and it is the one read back on mount.
  // Both run on every turn — see ai-integration/agentConversation.js for why
  // they are not the same thing.
  const storeRef = useRef(null);
  if (storeRef.current === null) {
    storeRef.current = createAgentConversationStore({
      createConversation: createAgentConversation,
      appendMessage: appendAgentMessage,
      getConversation: getAgentConversation,
      userName: ctx?.user?.username || ctx?.user?.email || "",
    });
  }

  // Restore on mount — the path GatewayAIPopover has never had. Until now the
  // drawer's survival across in-app navigation was an accident of router
  // reconciliation: nothing ever read a saved conversation back, so F5, a
  // browser restart or a hop to the editor lost the thread.
  //
  // Runs once, not on `open`: the drawer is mounted unconditionally by
  // GatewayApp, and re-resuming every time it is toggled would fight whatever
  // the operator is in the middle of.
  const [restoring, setRestoring] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const activeId = readActiveConversationId();
    if (!activeId) {
      setRestoring(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      const messages = await storeRef.current.resume(activeId);
      if (cancelled) return;
      // A null result means the thread is gone (archived, or a stale id after
      // a database reset). Starting fresh is the honest answer; an error
      // banner would blame the operator for a link they did not break.
      if (messages && messages.length > 0) setConversation(messages);
      setRestoring(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The conversation switcher: recent threads on this surface, plus New. Kept
  // deliberately small — the full history browser is the gateway's Assistant
  // History page, and this is only "put me back in the one I was in".
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [recentConversations, setRecentConversations] = useState([]);
  const [switcherError, setSwitcherError] = useState("");
  useEffect(() => {
    if (!switcherOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const page = await listAgentConversations({ surface: "gateway", limit: 10 });
        if (cancelled) return;
        setRecentConversations(Array.isArray(page?.conversations) ? page.conversations : []);
        setSwitcherError("");
      } catch (err) {
        if (cancelled) return;
        setRecentConversations([]);
        setSwitcherError(err?.message || "Recent chats could not be loaded.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [switcherOpen]);

  const handleResumeConversation = async (conversationId) => {
    setSwitcherOpen(false);
    const messages = await storeRef.current.resume(conversationId);
    if (!messages) {
      setServerError("That conversation could not be opened.");
      return;
    }
    setConversation(messages);
    setStatusMessage(null);
    setServerError(null);
    setAttachments([]);
    // The log is a separate record: resuming a thread starts a new investigation
    // row rather than reopening one that was already written.
    recorderRef.current.reset();
  };

  // Which model answered is part of the record: a bad answer has to be
  // traceable to the model that produced it. Resolved once per drawer open,
  // and a failure here simply leaves the fields blank.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [preference, providers] = await Promise.all([
          getAiPreference(),
          listAiProviders(),
        ]);
        if (cancelled) return;
        const provider = (providers || []).find((p) => p.id === preference?.provider_id);
        const providerName = provider?.name || preference?.provider_id || "";
        const modelName = preference?.model || provider?.default_model || "";
        recorderRef.current.setModel(providerName, modelName);
        storeRef.current.setModel(providerName, modelName);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Queue of follow-up user messages to send after Approve/Reject clicks. A
  // queue (not a single ref) so two cards approved in quick succession both
  // get their summary turn — the previous single-ref design lost the first
  // value when the second overwrote it before fireFollowUp ran.
  const followUpQueueRef = useRef([]);
  const drainingFollowUpsRef = useRef(false);

  useEffect(() => {
    if (open && textareaRef.current) textareaRef.current.focus();
  }, [open]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [prompt]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversation, statusMessage]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (event.key === "Escape" && !isLoading && !settingsOpen) {
        onClose?.();
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        handleSend();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prompt, isLoading, settingsOpen]);

  const handleStop = () => {
    if (abortRef.current) abortRef.current.abort();
    // Stop also wipes any queued follow-ups + pending tokens — otherwise an
    // approval card from an aborted turn could still trigger a new turn.
    followUpQueueRef.current = [];
    try {
      clearForPopover(popoverIdRef.current);
    } catch {}
  };

  const handleClear = () => {
    setConversation([]);
    setStatusMessage(null);
    setServerError(null);
    setAttachments([]);
    followUpQueueRef.current = [];
    // Clear starts a new record instead of wiping the old one: the whole
    // point of the log is that yesterday's conversation is still there. The
    // resumable thread gets the same treatment — a new id, and the stored
    // "active" pointer dropped so a reload does not resurrect what was just
    // cleared.
    recorderRef.current.reset();
    storeRef.current.startNew();
    try {
      clearForPopover(popoverIdRef.current);
    } catch {}
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > MAX_ATTACHMENT_BYTES) return;
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments((prev) => [
          ...prev,
          { dataUrl: reader.result, name: file.name },
        ]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async (arg = null) => {
    // Only a real programmatic follow-up (Approve/Reject dispatch) counts as
    // an override. Guard against a DOM/synthetic event leaking in when this is
    // bound straight to onClick — a truthy event must NOT be read as an
    // override, or the user's input/attachments never clear on send.
    const override =
      arg && typeof arg === "object" && !("nativeEvent" in arg) ? arg : null;
    const text = override?.userMessage ?? prompt.trim();
    const useAttachments = override ? [] : attachments;
    if (!text && useAttachments.length === 0) return;
    // Override (follow-up after Approve/Reject) bypasses isLoading: the
    // user-input pipeline is locked during dispatch, but the programmatic
    // follow-up MUST be allowed through.
    if (isLoading && !override) return;
    if (!override) {
      setPrompt("");
      setAttachments([]);
    }
    setStatusMessage("Preparing…");
    setIsLoading(true);
    setServerError(null);

    const userEntry = override?.silentUser
      ? null
      : {
          role: "user",
          content: text,
          ...(useAttachments.length > 0
            ? {
                attachments: useAttachments.map((a) => ({
                  dataUrl: a.dataUrl,
                  name: a.name,
                })),
              }
            : {}),
        };
    const historyBefore = conversation;
    const newConv = userEntry ? [...historyBefore, userEntry] : historyBefore;
    if (userEntry) setConversation(newConv);

    // Open the turn in the log and save immediately, so the conversation
    // exists on the server from the moment it starts rather than only once an
    // answer comes back. `logged` is filled in by whichever branch below wins
    // and written once in the finally, so every exit path — answer, approval
    // card, abort, crash — leaves exactly one saved turn.
    const recorder = recorderRef.current;
    const store = storeRef.current;
    const attachmentNames = useAttachments.map((a) => a.name).filter(Boolean);
    recorder.startTurn({
      content: text || "(image attached)",
      attachmentNames,
    });
    recorder.save();
    // The question is persisted before the request goes out, so the thread is
    // resumable from the moment it starts rather than only once an answer
    // comes back. Not awaited: the store swallows its own failures and the
    // turn must not wait on the database.
    store.appendUser({ text: text || "(image attached)", attachmentNames });
    const logged = { content: "", error: "", confirmation: null };

    try {
      if (!(await checkServerConfig())) {
        logged.content =
          "AI is not configured. An admin must set up the AI provider under Network → AI Settings (api-ai-settings).";
        setConversation([
          ...newConv,
          { role: "assistant", content: logged.content },
        ]);
        return;
      }

      const ac = new AbortController();
      abortRef.current = ac;
      const turnCtx = { ...ctx, popoverId: popoverIdRef.current };
      const result = await runGatewayAssistantTurn({
        userMessage: text || "(image attached)",
        history: historyBefore,
        imageDataUrls: useAttachments.map((a) => a.dataUrl),
        ctx: turnCtx,
        signal: ac.signal,
        onEvent: (evt) => {
          if (evt.type === "tool_start") {
            recorder.noteToolStart(evt.id, evt.name, evt.args);
            const label = describeToolEvent(evt.name, "start");
            if (label) setStatusMessage(label);
          } else if (evt.type === "tool_result") {
            recorder.noteToolResult(evt.id, evt.name, evt.result);
            const label = describeToolEvent(evt.name, "result");
            if (label) setStatusMessage(label);
          } else if (evt.type === "error") {
            setStatusMessage(`Error: ${evt.message}`);
          } else if (evt.type === "pending_confirmation") {
            setStatusMessage("Awaiting your approval…");
          }
        },
      });

      // If the AI proposed a mutation, render the approval card instead of
      // a final reply. The card's Approve/Reject handlers run the mutation
      // and auto-send a follow-up turn so the AI can summarize the result.
      if (result.reason === "awaiting_confirmation" && result.pendingConfirmation) {
        const pc = result.pendingConfirmation;
        // Why a non-empty content: sanitizeHistory in turn.js drops empty
        // assistant messages. Without this, the AI loses all memory of
        // having proposed an action — the next turn (after Approve/Reject)
        // would only see the user message and the followup, with no
        // assistant context.
        const summary = pc.proposed?.summary || {};
        const titleParts = [summary.title, summary.target].filter(Boolean);
        const narration = titleParts.length
          ? `${titleParts.join(" ")} — awaiting your approval.`
          : "Awaiting your approval.";
        logged.content = narration;
        // Recorded as pending; the Approve/Reject handler overwrites the
        // verdict and the next save carries it.
        logged.confirmation = confirmationFromPending(pc);
        setConversation([
          ...newConv,
          {
            role: "assistant",
            content: narration,
            pending: pc,
          },
        ]);
        return;
      }

      const reply =
        (result.ok && result.finalAnswerText) ||
        (result.reason === "aborted"
          ? "Stopped."
          : result.error
            ? `Error: ${result.error}`
            : result.reason === "max_iter"
              ? "I'm taking too many steps to finish this — say \"keep going\" and I'll continue from where I left off, or break the request into smaller pieces."
              : result.reason === "wall_time"
                ? "I took too long — try a more specific question."
                : result.finalAnswerText ||
                  "I didn't produce a response that turn. Try rephrasing — I do better with concrete asks like \"show me the database connections page\" or \"add a postgres connection\".");

      logged.content = reply;
      logged.error = result.ok ? "" : result.error || "";
      setConversation([
        ...newConv,
        {
          role: "assistant",
          content: reply,
          ...(result.finalAnswerText ? { finalAnswerText: result.finalAnswerText } : {}),
        },
      ]);
    } catch (err) {
      if (err?.name === "AbortError" || abortRef.current?.signal?.aborted) {
        logged.content = "Stopped.";
        setConversation([...newConv, { role: "assistant", content: "Stopped." }]);
        return;
      }
      logged.content = `Sorry — there was an error: ${err?.message || err}`;
      logged.error = err?.message || String(err);
      setConversation([
        ...newConv,
        { role: "assistant", content: logged.content },
      ]);
      setServerError(err?.message || String(err));
    } finally {
      abortRef.current = null;
      setIsLoading(false);
      setStatusMessage(null);
      recorder.finishTurn(logged);
      recorder.save();
      // The answer joins the resumable thread too. Tool calls come from the
      // recorder because it has already condensed each result to one line —
      // a resumed conversation should show that `list_datasources` returned 7
      // rows, not carry the seven rows around forever.
      const recorded = recorder.turns[recorder.turns.length - 1];
      store.appendAssistant({
        text: logged.content,
        status: logged.error ? "error" : "complete",
        error: logged.error,
        toolCalls: (recorded?.toolCalls || []).map((call) => ({
          name: call.name,
          ...(call.args !== undefined ? { args: call.args } : {}),
          ...(call.result ? { result: call.result } : {}),
          ...(typeof call.ok === "boolean" ? { ok: call.ok } : {}),
          ...(call.error ? { error: call.error } : {}),
        })),
        confirmations: logged.confirmation
          ? [
              {
                kind: logged.confirmation.kind || "",
                title: logged.confirmation.title || "",
                target: logged.confirmation.target || "",
                verdict: logged.confirmation.verdict || "pending",
              },
            ]
          : [],
      });
      // Drain any follow-ups queued during this turn (e.g. user approved a
      // confirmation while a turn was already in flight).
      if (followUpQueueRef.current.length > 0 && !drainingFollowUpsRef.current) {
        setTimeout(() => drainFollowUps(), 0);
      }
    }
  };

  // Drain follow-up messages one at a time. handleSend(override) bypasses the
  // isLoading guard, so without serialization two queued follow-ups would race.
  const drainFollowUps = async () => {
    if (drainingFollowUpsRef.current) return;
    drainingFollowUpsRef.current = true;
    try {
      while (followUpQueueRef.current.length > 0) {
        const next = followUpQueueRef.current.shift();
        // eslint-disable-next-line no-await-in-loop
        await handleSend({ userMessage: next, silentUser: true });
      }
    } finally {
      drainingFollowUpsRef.current = false;
    }
  };

  const enqueueFollowUp = (message) => {
    if (typeof message !== "string" || !message) return;
    followUpQueueRef.current.push(message);
    // Defer one tick so React re-renders (showing the resolved card state)
    // before we kick off the next turn.
    setTimeout(() => drainFollowUps(), 0);
  };

  // The operator's decision lands after the turn that raised the card was
  // already saved, so it is written as its own save.
  const recordVerdict = (token, verdict, outcome = {}) => {
    recorderRef.current.resolveConfirmation(token, verdict, outcome);
    recorderRef.current.save();
    // The verdict is its own message in the resumable thread: it lands after
    // the turn that raised the card was already appended, and a `tool` message
    // is how a resumed conversation knows a mutation was actually run without
    // putting a second bubble in the transcript.
    storeRef.current.appendAssistant({
      text: "",
      status: outcome.error ? "error" : "complete",
      error: outcome.error || "",
      confirmations: [
        {
          verdict,
          ...(outcome.result ? { result: outcome.result } : {}),
          ...(outcome.error ? { error: outcome.error } : {}),
        },
      ],
      // Only an approval that actually ran counts as a mutation, which is the
      // number the conversation list sorts an investigation by.
      mutations: verdict === "approved" && !outcome.error ? 1 : 0,
    });
  };

  // Approve: consume the token, run the mutation, queue a follow-up turn.
  // Throws on failure → the card transitions to its FAILED final state and
  // shows the error inline. Either way we queue a follow-up so the AI can
  // summarize the outcome (success OR failure) for the user.
  const handleApprove = async (token) => {
    const popoverId = popoverIdRef.current;
    const entry = consumePending(token, popoverId);
    if (!entry) {
      // No follow-up: the AI never got a chance to act, nothing to summarize.
      throw new Error("This proposal expired or was already handled.");
    }
    const summaries = [];
    const errors = [];
    for (const action of entry.actions) {
      // eslint-disable-next-line no-await-in-loop
      const result = await dispatchMutation(action.mutation);
      if (result.ok) summaries.push(result.summary);
      else errors.push(result.error || "Action failed.");
    }
    if (errors.length > 0) {
      const msg = errors.join(" • ");
      // Logged as approved even though it failed: the operator did approve,
      // and the error is what the investigation is looking for.
      recordVerdict(token, "approved", { error: msg });
      enqueueFollowUp(`User approved, but action failed: ${msg}`);
      throw new Error(msg);
    }
    const summary = summaries.join(" • ") || "Done.";
    recordVerdict(token, "approved", { result: summary });
    enqueueFollowUp(`User approved. Result: ${summary}`);
  };

  const handleReject = async (token) => {
    const popoverId = popoverIdRef.current;
    consumePending(token, popoverId);
    recordVerdict(token, "rejected");
    enqueueFollowUp("User declined the action.");
  };

  return (
    <aside
      className={`gwAIPopover ${open ? "gwAIPopoverOpen" : ""} ${isResizing ? "gwAIPopoverResizing" : ""}`}
      aria-hidden={!open}
    >
      <div
        className="gwAIResizer"
        onPointerDown={onResizerPointerDown}
        onPointerMove={onResizerPointerMove}
        onPointerUp={onResizerPointerUp}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize AI assistant"
      />
      {isResizing && <div className="gwAIResizeOverlay" />}
      <div className="gwAIHeader">
        <div className="gwAIHeaderText">
          <div className="gwAITitle">
            <img src={sparksIcon} alt="" className="gwAITitleIcon" />
            AI Assistant
          </div>
          <div className="gwAISubtitle">{emptyCopy.subtitle}</div>
        </div>
        <div className="gwAIHeaderActions">
          {/* Resume, or start a new thread. Small on purpose: the full history
              browser is the gateway's Assistant History page, and this only
              has to answer "put me back in the one I was in". */}
          <span className="gwAIConvAnchor">
            <button
              type="button"
              className={`gwAIHeaderBtn${switcherOpen ? " is-open" : ""}`}
              onClick={() => setSwitcherOpen((open) => !open)}
              title="Recent conversations"
              aria-haspopup="menu"
              aria-expanded={switcherOpen}
            >
              Chats
            </button>
            {switcherOpen && (
              <div className="gwAIConvMenu" role="menu" aria-label="Recent conversations">
                <button
                  type="button"
                  className="gwAIConvItem gwAIConvNew"
                  role="menuitem"
                  onClick={() => {
                    setSwitcherOpen(false);
                    handleClear();
                  }}
                >
                  New chat
                </button>
                {switcherError ? (
                  <div className="gwAIConvEmpty">{switcherError}</div>
                ) : recentConversations.length === 0 ? (
                  <div className="gwAIConvEmpty">No saved conversations yet.</div>
                ) : (
                  recentConversations.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      className="gwAIConvItem"
                      role="menuitem"
                      onClick={() => handleResumeConversation(row.id)}
                    >
                      <span className="gwAIConvTitle">{row.title || "Untitled chat"}</span>
                      <span className="gwAIConvMeta">
                        {row.ownerUserName || row.ownerUserId || ""}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </span>
          {conversation.length > 0 && (
            <button
              type="button"
              className="gwAIHeaderBtn"
              onClick={handleClear}
              title="Clear conversation"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className="gwAIHeaderBtn gwAIHeaderClose"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            <img src={xmarkIcon} alt="" />
          </button>
        </div>
      </div>

      <div className="gwAIBody">
        {restoring && conversation.length === 0 ? (
          // Restoring, not empty. Showing "How can I help?" for the beat it
          // takes to load a stored thread would read as "your conversation is
          // gone", which is exactly the impression this ticket removes.
          <div className="gwAIEmpty">
            <div className="gwAIEmptyText">Restoring your conversation…</div>
          </div>
        ) : conversation.length === 0 ? (
          <div className="gwAIEmpty">
            <div className="gwAIEmptyTitle">How can I help?</div>
            <div className="gwAIEmptyText">
              {emptyCopy.body}
            </div>
            <div className="gwAIExamples">
              {suggestedPrompts.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="gwAIExample"
                  onClick={() => setPrompt(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="gwAIMessages">
            {conversation.map((msg, i) => (
              <div
                key={i}
                className={`gwAIMessage ${
                  msg.role === "user" ? "gwAIMessageUser" : "gwAIMessageAssistant"
                }`}
              >
                <div className="gwAIMessageRole">{msg.role === "user" ? "You" : "AI"}</div>
                <div className={`gwAIMessageContent${msg.role === "assistant" ? " gwAIMd" : ""}`}>
                  {msg.attachments?.length > 0 && (
                    <div className="gwAIMessageAttachments">
                      {msg.attachments.map((att, j) => (
                        <img
                          key={j}
                          src={att.dataUrl}
                          alt={att.name || "attachment"}
                          className="gwAIMessageAttachmentImg"
                        />
                      ))}
                    </div>
                  )}
                  {msg.role === "assistant" ? (
                    msg.content && !msg.pending ? (
                      <>
                        <SimpleMarkdown text={msg.content} />
                      </>
                    ) : null
                  ) : (
                    msg.content
                  )}
                  {msg.pending ? (
                    <GatewayAIConfirmCard
                      token={msg.pending.confirmationToken}
                      proposed={msg.pending.proposed}
                      expiresAt={msg.pending.expiresAt}
                      onApprove={handleApprove}
                      onReject={handleReject}
                    />
                  ) : null}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="gwAIMessage gwAIMessageAssistant">
                <div className="gwAIMessageRole">AI</div>
                <div className="gwAIMessageContent gwAILoading">
                  <span className="gwAIDot" />
                  <span className="gwAIDot" />
                  <span className="gwAIDot" />
                  {statusMessage && <span className="gwAIStatus">{statusMessage}</span>}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
        {serverError && (
          <div className="gwAIError">
            <strong>Connection issue:</strong> {serverError}
          </div>
        )}
      </div>

      <div className="gwAIFooter">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <div className="gwAIInputWrap">
          <textarea
            ref={textareaRef}
            className="gwAIInput"
            placeholder={isLoading ? "Working — type a follow-up to redirect…" : "Ask about your gateway…"}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
          />
          <div className="gwAIInputActions">
            {attachments.length > 0 && (
              <div className="gwAIAttachments">
                {attachments.map((att, i) => (
                  <div key={i} className="gwAIAttachmentChip">
                    <img src={att.dataUrl} alt={att.name} className="gwAIAttachmentThumb" />
                    <span className="gwAIAttachmentName">{att.name}</span>
                    <button
                      type="button"
                      className="gwAIAttachmentRemove"
                      onClick={() => handleRemoveAttachment(i)}
                      aria-label={`Remove ${att.name}`}
                    >
                      <img src={xmarkIcon} alt="" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="gwAIInputActionsRight">
              <span className="gwAISettingsAnchor" ref={settingsAnchorRef}>
                <button
                  type="button"
                  className={`gwAIIconBtn${settingsOpen ? " is-open" : ""}`}
                  onClick={() => setSettingsOpen((v) => !v)}
                  title="AI model settings"
                  aria-label="AI model settings"
                  aria-expanded={settingsOpen}
                  aria-haspopup="dialog"
                >
                  <img src={settingsIcon} alt="" />
                </button>
                <GatewayAIModelSettings
                  open={settingsOpen}
                  onClose={() => setSettingsOpen(false)}
                  anchorRef={settingsAnchorRef}
                />
              </span>
              <button
                type="button"
                className="gwAIIconBtn"
                onClick={handleAttachClick}
                title="Attach image"
                aria-label="Attach image"
              >
                <img src={attachmentIcon} alt="" />
              </button>
              {isLoading ? (
                <button
                  type="button"
                  className="gwAISend gwAIStop"
                  onClick={handleStop}
                  title="Stop the AI"
                  aria-label="Stop"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <rect x="3" y="3" width="10" height="10" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  className="gwAISend"
                  onClick={() => handleSend()}
                  disabled={!prompt.trim() && attachments.length === 0}
                  title="Send (⌘/Ctrl + Enter)"
                  aria-label="Send"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M8 13V3M3 7l5-5 5 5" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="gwAIHint">
          Press Enter to send, Shift+Enter for new line, Esc to close
        </div>
      </div>
    </aside>
  );
}
